import { FC, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  Box, Typography, Grid
} from "@mui/material"
import { useForm, Controller, FormProvider } from "react-hook-form"
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import AccessTariffSelectField from "../customFields/AccessTariffSelectField";
import { useTranslation } from "react-i18next";
import { RootState } from "@/store";
import { FormHelperText } from "@mui/material";
import GasProductSelectField from "./../customFields/GasProductSelectField";
import useI18n from "@/hooks/useI18n";
import { IStepForm, StepGasContractFormData } from "../../types/stepsFormData";
import { SubmitButtonContext } from "../../Contractation";
import { useGasProducts } from "../../hooks/data/useProducts";

const FormGasContract: FC<IStepForm<StepGasContractFormData>> = ({
  onSubmit
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  
  const { t } = useTranslation();
  const { currentLang } = useI18n();

  const contractation = useSelector((state: RootState) => state[`gasContractation`]);

  const onAboutGasContractSubmit = async (values: StepGasContractFormData) => {
    await onSubmit(values);
  };

  const schema = z.object({
    accessTariff: z.number({
      invalid_type_error: t('common:text.required_field')
    }).min(1, {message: t('common:text.required_field')}),
    selectedProduct: z.number({
      invalid_type_error: t('common:text.required_field')
    }).positive({message: t('common:text.required_field')}),
  }) satisfies z.ZodType<StepGasContractFormData>;

  const methods = useForm<StepGasContractFormData>({
    resolver: zodResolver(schema),
    defaultValues: contractation.steps.gasContract
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting }
  } = methods;

  const watchedAccessTariff = watch("accessTariff");
  const watchedSelectedProduct = watch("selectedProduct");

  const productsData = useGasProducts({
    accessTariff: watchedAccessTariff, 
    cups: contractation.steps.installation.cups, 
    lang: currentLang.value
  });

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {handleSubmit(onAboutGasContractSubmit)()},
      loading: isSubmitting,
      disabled: !!(Object.keys(errors).length)
    });
  }, [isSubmitting, !!(Object.keys(errors).length)]);

  useEffect(() => {
    if(productsData.isSuccess && productsData.data.length) {
      const currentSelectedProductId = productsData.data.find(prod => prod.id === getValues("selectedProduct"))?.id;
      if(currentSelectedProductId) {
        setValue("selectedProduct", currentSelectedProductId);
      } else {
        setValue("selectedProduct", -1);
      }
    }
  }, [productsData.data]);

  // Debounce data
  // useEffect(() => {
  //   const debounceForm = setTimeout(() => {
  //     const newValues = watch();
  //     for (const element in newValues) {
  //       if (gasContractration[element] !== newValues[element as keyof IStepGasContractFormData]) {
  //         console.debug("differs where ", gasContractration[element], " !== ", newValues[element as keyof IStepGasContractFormData] );
  //         dispatch(submitAboutGasContractData(newValues))
  //         return
  //       }
  //     }
  //   }, 2000);

  //   return () => clearTimeout(debounceForm);
  // }, [...Object.values(watch())]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onAboutGasContractSubmit)}>
        <Typography variant="h6" color="primary">
          {t('common:text.contractation_current_gas_contract_data')}
        </Typography>
        <Typography variant="body2" sx={{mb: 2}}>
          {t('common:text.contractacion_about_installation_info')}
        </Typography>
        <Box sx={{ml: {md: 5}, mr: {md: 5}}}>

          {(!contractation.steps.gasContract.sipsData || 
            !watchedAccessTariff) && 
            <>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {t('common:text.contract_title')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={8} md={6} mb={2}>
                <AccessTariffSelectField
                  gas={true}
                  label={t('common:text.contractation_access_tariff')}
                />
              </Grid>

            </>
          }
        </Box>

        {watchedAccessTariff &&
          <>
            <Typography variant="h6" color="primary">
              {t('common:text.contractation_new_pricelist')}
            </Typography>
            <Typography variant="body2" sx={{mb: 2}}>
              {t('common:text.contractation_current_pressure_warning')}
            </Typography>
            <Box sx={{ml:{md: 5}, mr:{md: 5}}}>
              <Controller
                name={"selectedProduct"}
                control={control}
                render={({ field }) => <GasProductSelectField
                  availableProducts={productsData.isSuccess ? productsData.data : []}
                  loading={productsData.isLoading}
                  input={field}
                />}
              />
              {!!(errors.selectedProduct) &&
                <FormHelperText error={true}>
                  {errors.selectedProduct.message}
                </FormHelperText>
              }
            </Box>
          </>
        }
      </form>
    </FormProvider>
  );
};

export default FormGasContract;
